import type { InsightCard, RatioAssessment } from '../../../data/ServerApiClient'
import { formatCurrencyCompact, formatImpact } from './cfoFormat'

// cfoMetricExplainer — turns a server InsightCard into (a) a plain-language
// explainer a non-financial reader understands, and (b) a chartable "health band"
// so the Home card and the Explain modal can always draw a gauge. It prefers the
// server-authored `explain`/`value_display`/params (authoritative, and it covers
// per-client custom metrics automatically), and falls back to a small curated
// library for the canonical ratios plus a generic derivation. Single source of
// truth for both the Home brief and the Explain modal.

export type MetricExplainer = {
  /** Plain, human title for the metric (falls back to the card headline/label). */
  friendlyName: string
  /** "What this is" in one plain sentence. */
  whatItIs: string | null
  /** "Why it matters" in one plain sentence. */
  whyItMatters: string | null
  /** "What good looks like" — the healthy band in plain words. */
  goodRange: string | null
  /** Human category, e.g. "Liquidity", "Bookkeeping". */
  category: string | null
  isDataQuality: boolean
  /** The better-fit metric the client's ledger prefers, when this was demoted. */
  replacementLabel: string | null
}

export type MetricHealthBand = {
  value: number
  healthyMin: number | null
  healthyMax: number | null
  higherIsBetter: boolean | null
  unit: string
  assessment: RatioAssessment
  /** Pre-formatted value, e.g. "-$882.3K". */
  display: string
  /** Pre-formatted band bounds, e.g. "$0" / "60%" / "1.5x". */
  minLabel: string | null
  maxLabel: string | null
}

const num = (v: unknown): number | null => {
  const n = typeof v === 'number' ? v : Number(v)
  return Number.isFinite(n) ? n : null
}

const trim = (v: unknown): string => String(v ?? '').trim()

/** Friendlier, plain-English titles for the terse canonical ratios. Custom
 *  per-client metrics keep their (already human) ledger label. */
const FRIENDLY_NAME: Record<string, string> = {
  current_ratio: 'Can you cover short-term bills?',
  quick_ratio: 'Cash-like assets vs. bills due',
  working_capital: 'Short-term cushion',
  debt_to_assets: 'Share of assets funded by debt',
  debt_to_equity: 'Debt vs. owner equity',
  interest_coverage: 'Can profit cover the interest?',
  gross_margin: 'Gross margin',
  net_margin: 'Bottom-line margin',
  operating_margin: 'Operating margin',
  days_sales_outstanding: 'How fast you collect',
  days_cash_on_hand: 'Days of cash on hand',
  cash_conversion_cycle: 'Cash conversion cycle',
}

/** Curated plain-language "what it is" for the canonical ratios, in a warmer voice
 *  than the terse server description. Server text still wins when present. */
const CURATED_WHAT: Record<string, string> = {
  current_ratio: 'For every $1 of bills due soon, how many dollars of short-term assets you have to pay them.',
  quick_ratio: 'The same idea as the current ratio, but only counting cash and near-cash — a stricter test.',
  working_capital: 'Short-term assets minus short-term bills — the dollar buffer you run day-to-day on.',
  debt_to_assets: 'The share of everything the practice owns that is funded by debt rather than by the owners.',
  debt_to_equity: 'How much the practice leans on borrowed money versus owner money.',
  interest_coverage: 'How many times over your operating profit could pay this period’s interest bill.',
}

const CATEGORY_LABEL: Record<string, string> = {
  liquidity: 'Liquidity',
  leverage: 'Leverage',
  efficiency: 'Efficiency',
  profitability: 'Profitability',
  coverage: 'Coverage',
  bookkeeping: 'Bookkeeping',
}

const humanizeId = (id: string): string =>
  id
    .split('_')
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ')

export const severityToAssessment = (sev: InsightCard['severity']): RatioAssessment => {
  switch (sev) {
    case 'bad':
      return 'concern'
    case 'watch':
    case 'check':
      return 'watch'
    case 'good':
      return 'healthy'
    default:
      return 'neutral'
  }
}

const drillParams = (card: InsightCard): Record<string, unknown> =>
  (card.drill_target?.params as Record<string, unknown>) || {}

/** Unit key for a card: server params.unit, else a small canonical map, else ''. */
const RATIO_UNIT_BY_ID: Record<string, string> = {
  current_ratio: 'times',
  quick_ratio: 'times',
  debt_to_equity: 'times',
  interest_coverage: 'times',
  debt_to_assets: 'ratio',
  return_on_equity: 'ratio',
  return_on_assets: 'ratio',
  gross_margin: 'ratio',
  net_margin: 'ratio',
  operating_margin: 'ratio',
  days_sales_outstanding: 'days',
  days_cash_on_hand: 'days',
  cash_conversion_cycle: 'days',
}

const unitForCard = (card: InsightCard): string => {
  const fromParams = trim(drillParams(card).unit)
  if (fromParams) return fromParams
  return RATIO_UNIT_BY_ID[trim(card.impact?.metric_id)] || (card.impact?.unit === 'usd' ? 'currency' : 'ratio')
}

/** Format a single value in a unit, for band-edge labels. */
export const formatByUnit = (value: number, unitKey: string): string => {
  switch (unitKey) {
    case 'currency':
      return formatCurrencyCompact(value)
    case 'percent':
    case 'ratio': {
      const pct = value * 100
      return `${Number.isInteger(pct) ? pct.toFixed(0) : pct.toFixed(1)}%`
    }
    case 'times':
      return `${value.toFixed(value % 1 === 0 ? 0 : 1)}x`
    case 'days':
      return `${value.toFixed(0)} days`
    default:
      return String(value)
  }
}

/** Chartable value-vs-band for a card, or null when there is no band to plot. */
export function healthBandFromCard(card: InsightCard): MetricHealthBand | null {
  const params = drillParams(card)
  const value = num(card.impact?.value)
  if (value == null) return null
  const healthyMin = num(params.healthy_min)
  const healthyMax = num(params.healthy_max)
  if (healthyMin == null && healthyMax == null) return null
  const unit = unitForCard(card)
  const higherIsBetter = typeof params.higher_is_better === 'boolean' ? params.higher_is_better : null
  return {
    value,
    healthyMin,
    healthyMax,
    higherIsBetter,
    unit,
    assessment: severityToAssessment(card.severity),
    display: trim(card.value_display) || formatImpact(card),
    minLabel: healthyMin != null ? formatByUnit(healthyMin, unit) : null,
    maxLabel: healthyMax != null ? formatByUnit(healthyMax, unit) : null,
  }
}

/** The plain-language explainer for a card. */
export function explainerForCard(card: InsightCard): MetricExplainer {
  const metricId = trim(card.impact?.metric_id)
  const explain = card.explain || null
  const isDataQuality = !!explain?.is_data_quality || (card.tags || []).includes('data_quality')

  // Strip the verdict clause from the server headline to recover a clean title
  // ("Cash Net of Patient Prepayments", "Legacy & Uncategorized P&L Noise"). The
  // "Bookkeeping" nature is conveyed by the category chip + severity, not the name.
  const cleanHeadline = trim(card.headline)
    .replace(/:\s*bookkeeping to clean up$/i, '')
    .replace(/ is (outside its healthy range|lower than it should be|higher than it should be).*$/i, '')
  const friendlyName =
    FRIENDLY_NAME[metricId] || cleanHeadline || humanizeId(metricId) || 'This metric'

  const whatItIs =
    trim(explain?.what_it_is) || CURATED_WHAT[metricId] || null

  const whyItMatters = trim(explain?.why_it_matters) || null

  const goodRange = trim(explain?.good_range) || null

  const categoryKey = trim(card.category)
  const category =
    trim(explain?.category) || CATEGORY_LABEL[categoryKey] || (categoryKey ? humanizeId(categoryKey) : null)

  return {
    friendlyName,
    whatItIs,
    whyItMatters,
    goodRange,
    category,
    isDataQuality,
    replacementLabel: trim(explain?.replacement_label) || null,
  }
}
